import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin, X, Loader2, ChevronDown } from "lucide-react";
import { Input } from "@tabipla/ui";
import { cn } from "@tabipla/ui/lib/utils";
import {
  usePlaceAutocomplete,
  type PlaceResult,
  type AutocompleteSuggestion,
  type ApiClientInterface,
} from "@tabipla/hooks";

export type { PlaceResult };

interface PlaceAutocompleteProps {
  value: string;
  onChange: (place: PlaceResult | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** API client for making requests */
  apiClient: ApiClientInterface;
  /** Custom function to format the display value after selection */
  formatDisplayValue?: (result: PlaceResult) => string;
}

export function PlaceAutocomplete({
  value,
  onChange,
  placeholder,
  disabled,
  apiClient,
  formatDisplayValue,
}: PlaceAutocompleteProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use the shared hook for place autocomplete
  const {
    suggestions,
    isLoading,
    isFetchingDetails,
    fetchSuggestions,
    selectSuggestion,
    clearSuggestions,
  } = usePlaceAutocomplete(apiClient);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update isOpen when suggestions change
  useEffect(() => {
    setIsOpen(suggestions.length > 0);
    setHighlightedIndex(-1);
  }, [suggestions]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the API call
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, 300);
    },
    [fetchSuggestions]
  );

  const handleSelectSuggestion = useCallback(
    async (suggestion: AutocompleteSuggestion) => {
      setIsOpen(false);
      setInputValue(suggestion.mainText);

      const result = await selectSuggestion(suggestion);
      if (result) {
        const displayValue = formatDisplayValue ? formatDisplayValue(result) : result.name;
        setInputValue(displayValue);
        onChange(result);
      } else {
        toast.error(t("spots.placeDetailError", "場所の詳細を取得できませんでした"));
      }
    },
    [selectSuggestion, onChange, t, formatDisplayValue]
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    clearSuggestions();
    setIsOpen(false);
    onChange(null);
    inputRef.current?.focus();
  }, [onChange, clearSuggestions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSelectSuggestion(suggestions[highlightedIndex]);
          }
          break;
        case "Escape":
          setIsOpen(false);
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handleSelectSuggestion]
  );

  const showLoading = isLoading || isFetchingDetails;

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder || t("spots.locationPlaceholder")}
          disabled={disabled || isFetchingDetails}
          className="pl-9 pr-16"
        />
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {showLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : inputValue ? (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </div>

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-md">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                "flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent",
                highlightedIndex === index && "bg-accent"
              )}
            >
              <span className="font-medium">{suggestion.mainText}</span>
              {suggestion.secondaryText && (
                <span className="text-xs text-muted-foreground">{suggestion.secondaryText}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
